import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import {
  X,
  Star,
  Trash2,
  AlertCircle,
  Check,
  CheckCircle2,
  Loader2,
  Layers,
  Plus,
  Pencil,
  Info,
  ChevronRight,
} from "lucide-react";
import VariantImageManager from "./VariantImageManager";
import { isHeicFile, isWebpFile, isAvifFile, isUnsupportedUploadFile, UNSUPPORTED_FORMAT_MESSAGE, convertHeicToJpeg, convertAvifToJpeg } from "../utils/heicConvert";
import { useBulkUpload } from "../hooks/useBulkUpload";
import {
  MAX_IMAGENES_POR_PRODUCTO,
  mensajeRecorte,
  recortarAlTope,
} from "../lib/limiteImagenesProducto";
import { AgregarVariosModal } from "./AgregarVariosModal";
import { AbanicoDeFotos, TarjetaDeAgregarFoto, abrirConTeclado } from "./AbanicoDeFotos";

export interface ProductImage {
  id: number;
  url: string;
  principal: boolean;
  orden: number;
  producto_variante_id: number;
  variante_id?: string;
  tempFile?: File; // For temporary files before upload
  fileName?: string; // Original filename for saving
}

export interface Variant {
  id: string;
  nombre: string;
  image_url?: string;
  images?: ProductImage[];
  created_at?: string; // Database timestamp for ordering variants
}

interface VariantFormProps {
  editingVariant: Variant | null;
  newProduct: { nombre: string; images: ProductImage[] };
  loading: boolean;
  uploadingImages: Record<string, boolean>;
  imageError: string | null;
  onCancel: () => void;
  onSave: () => void; // This will be our unified save function for both adding and updating
  onImageUpload: (variantId: string, files: File[]) => void | Promise<void>;
  onDeleteImage: (variantId: string, imageId: number) => void;
  onSetPrincipalImage: (variantId: string, imageId: number) => void;
  onReorderImages: (
    variantId: string,
    imageId: number,
    direction: "up" | "down",
  ) => void;
  setEditingVariant: React.Dispatch<React.SetStateAction<Variant | null>>;
  setNewProduct: React.Dispatch<
    React.SetStateAction<{ nombre: string; images: ProductImage[] }>
  >;
  onDeleteVariant?: (variantId: string) => void;
  rubro?: string;
  // Bulk upload (only relevant when !editingVariant)
  idCompany?: string | null;
  userPlan?: string;
  onBulkSuccess?: () => Promise<void> | void;
  brandId?: string | null;
  /** Algo más para mostrar al editar, debajo de las imágenes (p. ej. la ficha de Mercado Libre). */
  extraEdicion?: React.ReactNode;
}

/** Extensiones de foto que se aceptan cuando el archivo llega sin `type`. */
const EXTENSIONES_DE_FOTO = /\.(jpe?g|png|webp|avif|heic|heif)$/i;

// Temporary Image Manager component for handling images before uploading
export const TempImageManager = ({
  images,
  imageError,
  onChange,
  rubro,
  debajoDeLaZona,
}: {
  images: ProductImage[];
  imageError: string | null;
  onChange: (images: ProductImage[]) => void;
  rubro?: string;
  /** Va pegado abajo de la zona de subida mientras no hay fotos ("¿Tenés varios productos?"). */
  debajoDeLaZona?: React.ReactNode;
}) => {
  // Determine which images and instructions to show based on rubro
  const isFashion = rubro === "Moda e Indumentaria";
  const isHomeDecor = rubro === "Hogar y Decoración";

  const getExampleImages = () => {
    if (isFashion) {
      return {
        img1: "https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/bolso.jpeg",
        img2: "https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/bolso_siendo_usado.png",
        alt1: "Ejemplo de foto de producto",
        alt2: "Ejemplo de foto de producto siendo utilizado",
      };
    } else if (isHomeDecor) {
      return {
        img1: "https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/sillon.png",
        img2: "https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/sillon_casa.png",
        alt1: "Ejemplo de foto de producto",
        alt2: "Ejemplo de foto de producto en contexto",
      };
    } else {
      return {
        img1: "https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/botella.png",
        img2: "https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/botella_uso.png",
        alt1: "Ejemplo de foto de producto",
        alt2: "Ejemplo de foto de producto en contexto",
      };
    }
  };

  const exampleImages = getExampleImages();
  const [showLastImageWarning, setShowLastImageWarning] = useState(false);
  const [convertingHeic, setConvertingHeic] = useState(false);
  const topeAlcanzado = images.length >= MAX_IMAGENES_POR_PRODUCTO;
  // Acepta N archivos (multi-selección del picker): son fotos del MISMO
  // producto —distintos ángulos—, así que se agregan todas juntas a la lista
  // temporal. `onChange` se llama UNA vez con el lote completo: llamarlo por
  // archivo partiría de `images` (prop) sin actualizar y solo sobreviviría el
  // último. Una imagen rechazada no descarta las demás.
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Copia sincrónica: al limpiar el input el FileList del evento se vacía.
    const picked = Array.from(e.target.files ?? []);
    e.target.value = ""; // Reset input
    void agregarArchivos(picked);
  };

  // La zona de subida también acepta soltar archivos: se ve como un lugar donde
  // arrastrar (ADR-2026-10-06-mi-marca-alta-de-producto-con-su-cara).
  const [arrastrando, setArrastrando] = useState(false);
  const zonaDeSoltar = {
    onDragOver: (e: React.DragEvent) => {
      if (topeAlcanzado || convertingHeic) return;
      e.preventDefault();
      setArrastrando(true);
    },
    onDragLeave: () => setArrastrando(false),
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      setArrastrando(false);
      if (topeAlcanzado || convertingHeic) return;
      void agregarArchivos(Array.from(e.dataTransfer.files ?? []));
    },
  };

  const agregarArchivos = async (picked: File[]) => {
    if (picked.length === 0) return;

    // Validate file size (limit: 100 MB)
    const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
    const rejected: string[] = [];
    const nuevas: ProductImage[] = [];

    // Sólo imágenes, y antes del tope para que lo que no es foto no ocupe un lugar. El
    // `accept` del input no aplica a lo que se suelta en la zona: un PDF entraba como
    // "foto" y el guardado fallaba con el producto ya creado
    // (ADR-2026-10-06-mi-marca-alta-solo-acepta-imagenes).
    // Sin `type` (pasa con WebP/AVIF/HEIC en algunos sistemas) decide la extensión.
    const imagenes = picked.filter((file) => {
      const esFoto = file.type
        ? file.type.startsWith("image/")
        : EXTENSIONES_DE_FOTO.test(file.name);
      if (esFoto || isHeicFile(file)) return true;
      rejected.push(`${file.name}: ${UNSUPPORTED_FORMAT_MESSAGE}`);
      return false;
    });

    // Tope de 4 por producto. Se recorta ANTES del loop a propósito: convertir un
    // HEIC de 12 MP que después no va a entrar es medio segundo de CPU tirado.
    const { aceptadas: aProcesar, rechazadas: sobrantes } = recortarAlTope(
      images.length,
      imagenes,
    );
    const avisoTope = mensajeRecorte(sobrantes.length);
    if (avisoTope) rejected.push(avisoTope);

    for (const file of aProcesar) {
      if (file.size > MAX_FILE_SIZE) {
        rejected.push(
          `${file.name}: el archivo supera el límite de 100 MB. Reducí el tamaño e intentá de nuevo.`,
        );
        continue;
      }
      // Formatos bloqueados (RAW/TIFF/GIF/SVG — ADR-590): mismo patrón que
      // el límite de tamaño.
      if (isUnsupportedUploadFile(file)) {
        rejected.push(`${file.name}: ${UNSUPPORTED_FORMAT_MESSAGE}`);
        continue;
      }

      try {
        // Check if the file is a HEIC image
        let processedFile = file;
        let processedUrl = "";

        // AVIF también se convierte, como en la subida masiva (`useBulkUpload`):
        // no todo el pipeline lo procesa (ADR-2026-10-06-mi-marca-alta-convierte-avif).
        if (isHeicFile(file) || isWebpFile(file) || isAvifFile(file)) {
          try {
            setConvertingHeic(true);
            const converted = isAvifFile(file)
              ? await convertAvifToJpeg(file, 0.8)
              : await convertHeicToJpeg(file, 0.8);
            processedFile = converted.file;
            processedUrl = converted.url;
          } catch (err) {
            throw new Error(
              "No pudimos convertir la imagen. Probá con una imagen en formato png o jpg",
            );
          } finally {
            setConvertingHeic(false);
          }
        } else {
          processedUrl = URL.createObjectURL(file);
        }

        // Create new temp image entry. El offset con `nuevas.length` mantiene
        // únicos los IDs negativos temporales dentro del mismo lote.
        nuevas.push({
          id: -1 * (images.length + nuevas.length + 1),
          url: processedUrl,
          principal: false, // Never set new images as principal by default
          orden: images.length + nuevas.length + 1,
          producto_variante_id: 0,
          tempFile: processedFile,
        });
      } catch (error) {
        setConvertingHeic(false);
        console.error("Error processing image file:", error);
        rejected.push(
          `${file.name}: ${
            error instanceof Error
              ? error.message
              : "error al procesar la imagen."
          }`,
        );
      }
    }

    if (nuevas.length > 0) onChange([...images, ...nuevas]);
    if (rejected.length > 0) alert(rejected.join("\n"));
  };

  const handleDelete = (imageId: number) => {
    // Check if this is the last image
    if (images.length <= 1) {
      setShowLastImageWarning(true);
      return;
    }

    // Filter out the image to delete
    const updatedImages = images.filter((img) => img.id !== imageId);

    // If we deleted the principal image and there are no principal images left, set the first one as principal
    if (
      updatedImages.length > 0 &&
      !updatedImages.some((img) => img.principal)
    ) {
      updatedImages[0].principal = true;
    }

    onChange(updatedImages);

    // Revoke object URL to avoid memory leaks
    const deletedImg = images.find((img) => img.id === imageId);
    if (deletedImg && deletedImg.id < 0) {
      // Only for temp images
      URL.revokeObjectURL(deletedImg.url);
    }
  };

  const handleSetPrincipal = (imageId: number) => {
    const updatedImages = images.map((img) => ({
      ...img,
      principal: img.id === imageId,
    }));

    onChange(updatedImages);
  };

  // La que se guarda como principal: la marcada, o la primera si no hay ninguna (es lo
  // que hace el alta al guardar: `principal || i === 0`).
  const principalId = images.find((img) => img.principal)?.id ?? images[0]?.id;

  // Estilo de Mi marca (ADR-2026-10-06-mi-marca-alta-de-producto-con-su-cara): la zona de
  // subida es la tarjeta "Subir nuevo producto" de la grilla, con el mismo abanico; las
  // fotos cargadas, tarjetas `rounded-2xl` con las acciones encima, como el lápiz.
  return (
    <div>
      {/* Multi-selección de fotos del mismo producto. `accept` SOLO con MIME
          types: mezclarlos con extensiones (`.heic`) hace que iOS Safari y
          algunos pickers de Android ignoren `multiple` (misma nota que el
          input de "Subir varios productos", más abajo). */}
      <input
        type="file"
        id="temp-image-upload"
        accept="image/*,image/heic,image/heif"
        multiple
        className="hidden"
        // El `pointer-events-none` del label tapa el click, pero no el foco por
        // teclado (Enter sobre el label igual dispara el input).
        disabled={topeAlcanzado}
        onChange={handleFileSelect}
      />

      {images.length === 0 ? (
        <>
          {/* Zona de subida: tocar abre el selector, soltar archivos también sube. */}
          <label
            htmlFor="temp-image-upload"
            {...zonaDeSoltar}
            {...abrirConTeclado("temp-image-upload", convertingHeic)}
            className={`group relative flex cursor-pointer flex-col items-center justify-center gap-4 overflow-hidden rounded-3xl border-[1.5px] border-dashed px-5 py-8 text-center outline-none transition-[border-color,box-shadow,background-color] duration-300 focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 ${
              convertingHeic ? "pointer-events-none" : ""
            } ${
              arrastrando
                ? "border-brand-primary bg-[#EBEFFE] shadow-[0_12px_32px_-14px_rgba(22,68,235,0.45)]"
                : "border-[#C9D3FF] bg-gradient-to-b from-white to-[#F1F4FF] hover:border-brand-primary hover:shadow-[0_12px_32px_-14px_rgba(22,68,235,0.45)]"
            }`}
          >
            <span
              aria-hidden
              className="pointer-events-none absolute inset-x-0 top-0 h-2/3 bg-[radial-gradient(60%_70%_at_50%_0%,rgba(61,92,255,0.10),transparent)] opacity-70 transition-opacity duration-300 group-hover:opacity-100"
            />
            <AbanicoDeFotos cargando={convertingHeic} />
            <span className="relative">
              <span className="block text-[15px] font-semibold leading-tight text-[#16181D]">
                {convertingHeic ? "Preparando la foto…" : "Subí las fotos del producto"}
              </span>
              <span className="mt-1 block text-[13px] leading-snug text-black/45">
                <span className="sm:hidden">Tocá para elegirlas</span>
                <span className="hidden sm:inline">Hacé clic o arrastralas acá</span>
                {` · hasta ${MAX_IMAGENES_POR_PRODUCTO} fotos`}
              </span>
            </span>
          </label>
          {debajoDeLaZona && !convertingHeic && (
            <div className="mt-3">{debajoDeLaZona}</div>
          )}

          {/* Qué foto funciona: los dos ejemplos del rubro y el consejo. */}
          <div className="mt-5">
            <p className="mb-2.5 px-0.5 text-[13px] font-semibold text-[#16181D]">
              Así funcionan mejor
            </p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { src: exampleImages.img1, alt: exampleImages.alt1 },
                { src: exampleImages.img2, alt: exampleImages.alt2 },
              ].map((ej) => (
                <div
                  key={ej.src}
                  className="relative aspect-square overflow-hidden rounded-2xl bg-white"
                >
                  <img
                    src={ej.src}
                    alt={ej.alt}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                  {/* Borde encima de la foto: un ring del contenedor queda debajo de la
                      imagen y no se ve; éste la separa del fondo blanco. */}
                  <span aria-hidden className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-black/10" />
                  <span
                    aria-label="Correcta"
                    className="absolute bottom-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-success text-white shadow-[0_2px_6px_rgba(16,24,40,0.2)] ring-2 ring-white"
                  >
                    <Check className="h-3.5 w-3.5" strokeWidth={2.75} />
                  </span>
                </div>
              ))}
            </div>
            <ul className="mt-3 space-y-1.5 px-0.5 text-[13px] leading-snug text-black/55">
              <li className="flex gap-2">
                <Check className="mt-[3px] h-3.5 w-3.5 shrink-0 text-success" strokeWidth={2.5} />
                Que se vea claramente el producto que querés vender.
              </li>
              {isFashion && (
                <li className="flex gap-2">
                  <Check className="mt-[3px] h-3.5 w-3.5 shrink-0 text-success" strokeWidth={2.5} />
                  Las fotos del producto puesto (sin mostrar la cara) funcionan mejor.
                </li>
              )}
            </ul>
          </div>

        </>
      ) : (
        <>
          <div className="mb-2.5 flex items-baseline justify-between px-0.5">
            <p className="text-[13px] font-semibold text-[#16181D]">Fotos</p>
            <span
              className={`text-[12.5px] font-medium tabular-nums ${topeAlcanzado ? "text-[#16181D]" : "text-black/40"}`}
            >
              {images.length}/{MAX_IMAGENES_POR_PRODUCTO}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {images.map((image) => {
              const esPrincipal = image.id === principalId;
              return (
                <div
                  key={image.id}
                  className="group relative aspect-square overflow-hidden rounded-2xl bg-black/[0.04] motion-safe:animate-[aparecer_0.45s_ease-out_both]"
                >
                  <img
                    src={image.url}
                    alt="Imagen de producto"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                  <span aria-hidden className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-black/10" />
                  {esPrincipal && (
                    <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-black/55 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur-sm">
                      <Star className="h-3 w-3 fill-current" />
                      Principal
                    </span>
                  )}
                  {/* Acciones arriba a la derecha, círculos blancos como el lápiz de las
                      tarjetas de Mi marca. Siempre visibles: en el teléfono no hay hover. */}
                  <div className="absolute right-2 top-2 flex gap-1.5">
                    {!esPrincipal && (
                      <button
                        type="button"
                        onClick={() => handleSetPrincipal(image.id)}
                        title="Usar como principal"
                        aria-label="Usar como principal"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/95 text-black/70 shadow-md ring-1 ring-black/[0.06] backdrop-blur-md transition-all hover:bg-white hover:text-brand-primary active:scale-90"
                      >
                        <Star className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDelete(image.id)}
                      title="Quitar foto"
                      aria-label="Quitar foto"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/95 text-black/70 shadow-md ring-1 ring-black/[0.06] backdrop-blur-md transition-all hover:bg-white hover:text-error active:scale-90"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
            {/* Una foto más: la misma tarjeta punteada, chica, como última de la grilla. */}
            {!topeAlcanzado && (
              <TarjetaDeAgregarFoto
                htmlFor="temp-image-upload"
                cargando={convertingHeic}
                arrastrando={arrastrando}
                {...zonaDeSoltar}
              />
            )}
          </div>
          {topeAlcanzado && (
            <p className="mt-3 px-0.5 text-[13px] leading-snug text-black/50">
              Son las que la IA usa para generar. Quitá alguna si querés cambiarla.
            </p>
          )}
        </>
      )}

      {imageError && (
        <div className="mt-4 flex items-start gap-2 rounded-2xl bg-error-soft px-3.5 py-3 text-[13px] text-error-text">
          <AlertCircle className="mt-px h-4 w-4 shrink-0" />
          <span>{imageError}</span>
        </div>
      )}

      {/* Last Image Warning */}
      {showLastImageWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]">
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 text-center shadow-[0_24px_64px_-16px_rgba(16,24,40,0.35)] animate-modal-appear">
            <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-warning-soft">
              <AlertCircle className="h-5 w-5 text-warning" />
            </div>
            <h3 className="text-[17px] font-semibold text-[#16181D]">
              No se puede quitar
            </h3>
            <p className="mt-1 text-[14px] leading-snug text-black/55">
              El producto necesita al menos una foto.
            </p>
            <button
              type="button"
              onClick={() => setShowLastImageWarning(false)}
              className="mt-5 inline-flex h-10 w-full items-center justify-center rounded-full bg-brand-primary px-5 text-[14px] font-medium text-white transition-colors hover:bg-brand-primary-hover"
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Row for a bulk-upload item: shows name as plain text with pencil edit toggle.
// Clicking pencil switches to input; blur or Enter commits and returns to text.
const BulkRow = ({
  item,
  defaultName,
  saving,
  onRename,
  onRemove,
}: {
  item: import("../hooks/useBulkUpload").BulkItem;
  defaultName: string;
  saving: boolean;
  onRename: (name: string) => void;
  onRemove: () => void;
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.nombre);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const commit = () => {
    onRename(draft.trim());
    setEditing(false);
  };
  const cancel = () => {
    setDraft(item.nombre);
    setEditing(false);
  };

  const rowBorder =
    item.status === "error"
      ? "border-error/40 bg-error-soft/30"
      : item.status === "success"
        ? "border-green-300 bg-green-50"
        : item.status === "uploading"
          ? "border-brand-primary/30 bg-brand-primary/5"
          : "border-neutral-200 bg-white";

  const displayName = item.nombre.trim() || defaultName;
  const readOnly = saving || item.status === "success";

  return (
    <div
      className={`flex items-center gap-3 p-2 rounded-md border transition-colors ${rowBorder}`}
    >
      <div className="w-14 h-14 rounded-md overflow-hidden shrink-0 bg-neutral-100">
        <img
          src={item.previewUrl}
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      <div className="flex-1 min-w-0">
        {editing ? (
          <input
            ref={inputRef}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commit();
              } else if (e.key === "Escape") {
                e.preventDefault();
                cancel();
              }
            }}
            placeholder={defaultName}
            maxLength={100}
            className="w-full px-2 py-1 text-[14px] rounded border border-brand-primary bg-white focus:ring-1 focus:ring-brand-primary focus:border-brand-primary outline-none"
          />
        ) : (
          <div className="flex items-center gap-2 min-w-0">
            <span
              className="text-[14px] font-medium text-neutral-900 truncate"
              title={displayName}
            >
              {displayName}
            </span>
            {!readOnly && (
              <button
                type="button"
                onClick={() => {
                  setDraft(item.nombre);
                  setEditing(true);
                }}
                className="shrink-0 w-6 h-6 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-brand-primary hover:bg-neutral-100 transition-colors"
                aria-label="Editar nombre"
                title="Editar nombre"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
        {item.status === "error" && item.error && (
          <p className="text-[11px] text-error mt-0.5 truncate">{item.error}</p>
        )}
      </div>
      <div className="shrink-0 w-7 h-7 flex items-center justify-center">
        {item.status === "uploading" && (
          <Loader2 className="w-4 h-4 animate-spin text-brand-primary" />
        )}
        {item.status === "success" && (
          <CheckCircle2 className="w-5 h-5 text-green-600" />
        )}
        {(item.status === "pending" || item.status === "error") && !saving && (
          <button
            type="button"
            onClick={onRemove}
            className="w-7 h-7 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-error hover:bg-error-soft transition-colors"
            aria-label="Eliminar imagen"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

// Row para un grupo de archivos detectados por stem común. La cantidad
// de archivos y el status se derivan del grupo; el resto es similar a
// `BulkRow` (preview cuadrada + nombre editable + remove).
const BulkGroupRow = ({
  group,
  name,
  saving,
  onRename,
  onRemove,
}: {
  group: import("../utils/groupFilesByStem").FileGroup<
    import("../hooks/useBulkUpload").BulkItem
  >;
  name: string;
  saving: boolean;
  onRename: (name: string) => void;
  onRemove: () => void;
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  // El status del grupo: todos los items deberían compartirlo, pero por
  // si quedan desincronizados al toggle-ear, tomamos el peor (error
  // domina, después uploading, después pending, después success).
  const aggregateStatus = (() => {
    const statuses = group.files.map((it) => it.status);
    if (statuses.includes("error")) return "error" as const;
    if (statuses.includes("uploading")) return "uploading" as const;
    if (statuses.every((s) => s === "success")) return "success" as const;
    return "pending" as const;
  })();
  const aggregateError = group.files.find((it) => it.error)?.error;

  const rowBorder =
    aggregateStatus === "error"
      ? "border-error/40 bg-error-soft/30"
      : aggregateStatus === "success"
        ? "border-green-300 bg-green-50"
        : aggregateStatus === "uploading"
          ? "border-brand-primary/30 bg-brand-primary/5"
          : "border-neutral-200 bg-white";

  const readOnly = saving || aggregateStatus === "success";
  const previewUrl = group.files[0]?.previewUrl;

  const commit = () => {
    onRename(draft.trim());
    setEditing(false);
  };
  const cancel = () => {
    setDraft(name);
    setEditing(false);
  };

  return (
    <div
      className={`flex items-center gap-3 p-2 rounded-md border transition-colors ${rowBorder}`}
    >
      <div className="relative w-14 h-14 rounded-md overflow-hidden shrink-0 bg-neutral-100">
        {previewUrl && (
          <img
            src={previewUrl}
            alt=""
            className="w-full h-full object-cover"
          />
        )}
        {group.files.length > 1 && (
          <span
            className="absolute bottom-0.5 right-0.5 inline-flex items-center justify-center min-w-[20px] h-[18px] px-1 rounded-full bg-black/65 text-white text-[10.5px] font-semibold leading-none"
            aria-label={`${group.files.length} imágenes`}
          >
            +{group.files.length - 1}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        {editing ? (
          <input
            ref={inputRef}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commit();
              } else if (e.key === "Escape") {
                e.preventDefault();
                cancel();
              }
            }}
            placeholder={group.baseName}
            maxLength={100}
            className="w-full px-2 py-1 text-[14px] rounded border border-brand-primary bg-white focus:ring-1 focus:ring-brand-primary focus:border-brand-primary outline-none"
          />
        ) : (
          <div className="flex items-center gap-2 min-w-0">
            <span
              className="text-[14px] font-medium text-neutral-900 truncate"
              title={name}
            >
              {name}
            </span>
            {!readOnly && (
              <button
                type="button"
                onClick={() => {
                  setDraft(name);
                  setEditing(true);
                }}
                className="shrink-0 w-6 h-6 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-brand-primary hover:bg-neutral-100 transition-colors"
                aria-label="Editar nombre"
                title="Editar nombre"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
        <p className="text-[11.5px] text-neutral-500 mt-0.5">
          {group.files.length === 1
            ? "1 imagen"
            : `${group.files.length} imágenes agrupadas`}
        </p>
        {aggregateStatus === "error" && aggregateError && (
          <p className="text-[11px] text-error mt-0.5 truncate">
            {aggregateError}
          </p>
        )}
      </div>
      <div className="shrink-0 w-7 h-7 flex items-center justify-center">
        {aggregateStatus === "uploading" && (
          <Loader2 className="w-4 h-4 animate-spin text-brand-primary" />
        )}
        {aggregateStatus === "success" && (
          <CheckCircle2 className="w-5 h-5 text-green-600" />
        )}
        {(aggregateStatus === "pending" || aggregateStatus === "error") &&
          !saving && (
            <button
              type="button"
              onClick={onRemove}
              className="w-7 h-7 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-error hover:bg-error-soft transition-colors"
              aria-label="Eliminar grupo"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
      </div>
    </div>
  );
};

const VariantForm: React.FC<VariantFormProps> = ({
  editingVariant,
  newProduct,
  loading,
  uploadingImages,
  imageError,
  onCancel,
  onSave,
  onImageUpload,
  onDeleteImage,
  onSetPrincipalImage,
  onReorderImages,
  setEditingVariant,
  setNewProduct,
  onDeleteVariant,
  rubro,
  idCompany = null,
  userPlan = "gratis",
  onBulkSuccess,
  brandId = null,
  extraEdicion,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [animated, setAnimated] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [shakeKey, setShakeKey] = useState(0);
  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef(0);
  // "Subir varios" reemplaza el contenido del panel en vez de abrir un modal encima
  // (ADR-2026-10-06-subir-varios-adentro-del-panel). El alta se dibuja dos veces (panel de
  // escritorio y hoja de mobile); la vista de varios, en cambio, va en UN contenedor que
  // es panel o hoja según el ancho: así cruzar los 640px no la desmonta ni pierde la tanda
  // (ADR-2026-10-07-subir-varios-no-se-pierde-al-cambiar-de-ancho).
  const [vista, setVista] = useState<"alta" | "varios">("alta");
  const [variosOcupado, setVariosOcupado] = useState(false);
  const [variosTerminado, setVariosTerminado] = useState(false);

  // Bulk upload state (only used in add mode)
  const bulk = useBulkUpload({
    idCompany,
    userPlan,
    onSuccess: onBulkSuccess,
    brandId,
  });
  const inBulkMode = !editingVariant && bulk.items.length > 0;

  useEffect(() => {
    requestAnimationFrame(() => requestAnimationFrame(() => setAnimated(true)));
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const handleClose = useCallback(() => {
    if (bulk.saving || variosOcupado) return; // block close mid-bulk-upload
    setAnimated(false);
    setTimeout(() => {
      // If we're adding a new product (not editing), clear temporary state
      if (!editingVariant) {
        try {
          newProduct.images.forEach((img) => {
            if (img.id < 0 && img.url && img.url.startsWith("blob:")) {
              URL.revokeObjectURL(img.url);
            }
          });
        } catch (e) {
          // No-op
        }
        setNewProduct({ nombre: "", images: [] });
        bulk.clearAll();
      }
      onCancel();
    }, 300);
  }, [editingVariant, newProduct, setNewProduct, onCancel, bulk, variosOcupado]);

  // Terminó "subir varios": los productos ya están en el catálogo, así que se cierra el
  // panel entero. Espera a que la tanda deje de estar ocupada (`handleClose` no cierra en
  // medio de un guardado).
  useEffect(() => {
    if (!variosTerminado || variosOcupado) return;
    setVariosTerminado(false);
    handleClose();
  }, [variosTerminado, variosOcupado, handleClose]);

  const subirVarios = (
    <AgregarVariosModal
      open
      enPanel
      companyId={idCompany}
      brandId={brandId}
      userPlan={userPlan}
      onClose={() => setVista("alta")}
      onCerrarPanel={handleClose}
      onOcupado={setVariosOcupado}
      // `onSaved` refresca (llega también con productos que fallaron); el panel se cierra
      // sólo cuando salió la tanda entera.
      onSaved={async () => {
        if (onBulkSuccess) await onBulkSuccess();
      }}
      onTerminado={() => setVariosTerminado(true)}
    />
  );
  const enVarios = !editingVariant && vista === "varios";

  const handleTouchStart = (e: React.TouchEvent) => {
    dragStartY.current = e.touches[0].clientY;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    const delta = e.touches[0].clientY - dragStartY.current;
    if (delta > 0) setDragY(delta);
  };
  const handleTouchEnd = () => {
    if (dragY > 100) handleClose();
    else setDragY(0);
  };

  // Determine if save is disabled (used for styling)
  const isSaveDisabled = editingVariant
    ? !editingVariant.nombre
    : inBulkMode
      ? bulk.items.length === 0 || bulk.saving || !idCompany
      : newProduct.images.length === 0 || loading;

  const handleSaveWithValidation = async () => {
    if (editingVariant) {
      if (!editingVariant.nombre?.trim()) {
        setNameError(true);
        setShakeKey((k) => k + 1);
        return;
      }
      onSave();
      return;
    }

    if (inBulkMode) {
      if (bulk.items.length === 0) return;
      const result = await bulk.saveAll();
      if (result === "all-success") {
        setTimeout(() => handleClose(), 1000);
      }
      return;
    }

    if (newProduct.images.length === 0) return;
    onSave();
  };

  const title = editingVariant
    ? "Editar producto"
    : inBulkMode
      ? "Agregar varios productos"
      : "Agregar producto";

  // Cantidad de productos a crear: cuando el toggle "Usar nombres" está
  // activo, es la cantidad de grupos detectados (no la de archivos).
  const bulkProductCount = bulk.useFileNames
    ? bulk.groups.length
    : bulk.items.length;

  const saveButtonLabel = editingVariant
    ? loading
      ? "Guardando..."
      : "Listo"
    : inBulkMode
      ? bulk.saving
        ? "Guardando..."
        : `Crear ${bulkProductCount} ${
            bulkProductCount === 1 ? "producto" : "productos"
          }`
      : loading
        ? "Guardando..."
        : "Guardar";

  const anySaving = loading || bulk.saving;

  /* ── Saving state (slides up in place of the form) ── */
  const savingState = (
    <div
      className={`absolute inset-0 flex flex-col items-center justify-center pointer-events-none transition-all duration-300 ease-out ${
        loading
          ? "opacity-100 translate-y-0 delay-150"
          : "opacity-0 translate-y-3"
      }`}
    >
      <Loader2 className="w-7 h-7 animate-spin text-brand-primary mb-3" />
      <p className="text-[17px] font-semibold text-[#16181D]">
        Guardando producto…
      </p>
      <p className="text-[14px] text-black/45 mt-1">
        Esto puede tardar unos segundos
      </p>
    </div>
  );

  // Whether the "Subir varios productos" entry point should be offered.
  // Only in add mode, when we have the required context (idCompany+onBulkSuccess),
  // and there are no single-product images already loaded.
  const canOfferBulk =
    !editingVariant &&
    !!idCompany &&
    !!onBulkSuccess &&
    newProduct.images.length === 0;

  const bulkContent = (
    <div className="space-y-3">
      {/* Header / limit hint */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-[length:var(--text-body)] font-medium text-neutral-700">
            Productos a crear
          </label>
          <span
            className={`text-[13px] font-medium ${
              bulk.items.length >= bulk.limit
                ? "text-error"
                : "text-neutral-500"
            }`}
          >
            {bulk.items.length}/{bulk.limit}
          </span>
        </div>
        <p className="text-[13px] text-neutral-500">
          {bulk.useFileNames
            ? "Cada archivo se sube como un producto, usando el nombre del archivo. Imágenes con nombres similares (ej. remera_1.png, remera_2.png) se agrupan automáticamente."
            : "Cada imagen se creará como un producto distinto. Podés editar el nombre ahora o después."}
          {bulk.isFreeLikePlan && (
            <span> Mejorá tu plan para subir hasta 50 por lote.</span>
          )}
        </p>
      </div>

      {/* Toggle "Usar nombres de archivo" — solo desktop. En mobile el
          espacio es ajustado y la mecánica (renombrar y agrupar) queda
          mejor con el flujo single-row de toda la vida. La aclaración
          larga vive en un tooltip que aparece al pasar el mouse por
          encima del título o del ícono (i). */}
      <div className="hidden sm:flex items-center gap-3 rounded-md border border-neutral-200 bg-white p-3">
        <div className="flex-1 min-w-0">
          <div className="group/info relative inline-flex items-center gap-1.5">
            <span
              id="bulk-use-filenames-label"
              className="text-[13.5px] font-semibold text-neutral-900 cursor-default"
            >
              Usar nombres de archivo
            </span>
            <Info
              className="w-3.5 h-3.5 text-neutral-400 hover:text-neutral-600 transition-colors"
              aria-label="Más información"
            />
            {/* Tooltip — visible al hover del wrapper (cubre título + ícono).
                Posicionado abajo a la izquierda, con flechita arriba. */}
            <div
              role="tooltip"
              className="pointer-events-none absolute left-0 top-full mt-2 z-20 w-[280px] rounded-md bg-neutral-900 text-white text-[12px] leading-snug px-3 py-2 shadow-lg opacity-0 translate-y-1 transition-all duration-150 group-hover/info:opacity-100 group-hover/info:translate-y-0"
            >
              <span
                aria-hidden="true"
                className="absolute -top-1 left-3 w-2 h-2 bg-neutral-900 rotate-45"
              />
              Cada producto toma el nombre del archivo (sin extensión).
              Archivos como{" "}
              <span className="font-mono">remera_negra_1.png</span>,{" "}
              <span className="font-mono">remera_negra-2.png</span> o{" "}
              <span className="font-mono">remera_negra(3).png</span> se
              agrupan como un solo producto.
            </div>
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={bulk.useFileNames}
          aria-labelledby="bulk-use-filenames-label"
          onClick={() => bulk.setUseFileNames(!bulk.useFileNames)}
          disabled={bulk.saving}
          className={`shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary/30 disabled:opacity-40 disabled:cursor-not-allowed ${
            bulk.useFileNames ? "bg-brand-primary" : "bg-neutral-300"
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
              bulk.useFileNames ? "translate-x-5" : "translate-x-0.5"
            }`}
          />
        </button>
      </div>

      {/* Global error / partial-fail banner */}
      {bulk.error && (
        <div className="flex items-start text-error-text bg-error-soft border border-error/20 rounded-md p-3">
          <AlertCircle className="w-4 h-4 mr-2 shrink-0 mt-0.5" />
          <span className="text-[13px]">{bulk.error}</span>
        </div>
      )}

      {/* Progress bar while saving */}
      {bulk.saving && bulk.progress.total > 0 && (
        <div>
          <div className="flex items-center justify-between mb-1 text-[13px] text-neutral-500">
            <span>
              Guardando {bulk.progress.done}/{bulk.progress.total}...
            </span>
          </div>
          <div className="w-full h-1.5 bg-neutral-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-primary transition-[width] duration-300 ease-out"
              style={{
                width: `${
                  bulk.progress.total === 0
                    ? 0
                    : Math.round(
                        (bulk.progress.done / bulk.progress.total) * 100,
                      )
                }%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Bulk items list — modo agrupado (cuando useFileNames) o el
          flujo single-row de toda la vida. */}
      <div className="space-y-2">
        {bulk.useFileNames
          ? bulk.groups.map((group) => (
              <BulkGroupRow
                key={group.key}
                group={group}
                name={bulk.groupDisplayName(group)}
                saving={bulk.saving}
                onRename={(name) => bulk.updateGroupName(group.key, name)}
                onRemove={() => bulk.removeGroup(group.key)}
              />
            ))
          : bulk.items.map((item, idx) => (
              <BulkRow
                key={item.id}
                item={item}
                defaultName={bulk.defaultNameFor(idx)}
                saving={bulk.saving}
                onRename={(name) => bulk.updateName(item.id, name)}
                onRemove={() => bulk.removeItem(item.id)}
              />
            ))}
      </div>

      {/* Add more button */}
      {bulk.items.length < bulk.limit && !bulk.saving && (
        <button
          type="button"
          onClick={bulk.openFilePicker}
          disabled={bulk.convertingHeic}
          className="w-full inline-flex items-center justify-center gap-2 h-10 border border-dashed border-neutral-300 rounded-md text-neutral-500 hover:border-neutral-400 hover:text-neutral-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {bulk.convertingHeic ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Plus className="w-4 h-4" />
          )}
          {bulk.convertingHeic ? "Convirtiendo..." : "Agregar más imágenes"}
        </button>
      )}
    </div>
  );

  // Nombre: input píldora como el buscador de Mi marca
  // (ADR-2026-10-06-mi-marca-alta-de-producto-con-su-cara).
  const campoNombre = (
    <div>
      <div className="mb-2 flex items-baseline justify-between px-0.5">
        <label
          htmlFor="variant-form-nombre"
          className="text-[13px] font-semibold text-[#16181D]"
        >
          Nombre
        </label>
        {!editingVariant && (
          <span className="text-[12.5px] text-black/40">Opcional</span>
        )}
      </div>
      <input
        id="variant-form-nombre"
        type="text"
        value={editingVariant ? editingVariant.nombre : newProduct.nombre}
        onChange={(e) => {
          if (editingVariant) {
            setEditingVariant({ ...editingVariant, nombre: e.target.value });
          } else {
            setNewProduct({ ...newProduct, nombre: e.target.value });
          }
        }}
        key={shakeKey}
        className={`h-[var(--btn-h)] w-full rounded-full border bg-white px-4 text-[length:var(--text-body)] text-[#16181D] shadow-[0_1px_2px_rgba(16,24,40,0.04)] outline-none transition-shadow placeholder:text-black/35 focus:border-brand-primary focus:ring-4 focus:ring-[#EBEFFE] ${nameError ? "border-error animate-shake" : "border-black/[0.08]"}`}
        onFocus={() => nameError && setNameError(false)}
        placeholder={
          editingVariant
            ? "Nombre del producto"
            : "Si lo dejás vacío, lo completa la IA"
        }
      />
    </div>
  );

  // En el alta van primero las fotos (son lo único obligatorio) y después el nombre;
  // al editar, el nombre arriba como siempre.
  const formContent = inBulkMode ? (
    bulkContent
  ) : editingVariant ? (
    <div className="space-y-6">
      {campoNombre}
      <div>
        <p className="mb-2 px-0.5 text-[13px] font-semibold text-[#16181D]">
          Fotos
        </p>
        <VariantImageManager
          variantId={editingVariant.id || ""}
          images={editingVariant.images || []}
          uploadingImages={uploadingImages}
          imageError={imageError}
          onUpload={onImageUpload}
          onDelete={onDeleteImage}
          onSetPrincipal={onSetPrincipalImage}
          onReorder={onReorderImages}
          showHeader={false}
        />
      </div>
      {extraEdicion}
    </div>
  ) : (
    <div className="space-y-6">
      <TempImageManager
        images={newProduct.images}
        imageError={imageError}
        onChange={(newImages) =>
          setNewProduct({ ...newProduct, images: newImages })
        }
        rubro={rubro}
        debajoDeLaZona={
          // Otro camino, al lado del principal: pegado abajo de la zona de subida.
          canOfferBulk ? (
            <button
              type="button"
              onClick={() => setVista("varios")}
              className="group flex w-full items-center gap-3 rounded-2xl border border-[#DCE3FF] bg-white px-3.5 py-3 text-left transition-[border-color,box-shadow] duration-300 hover:border-brand-primary hover:shadow-[0_10px_24px_-14px_rgba(22,68,235,0.45)]"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#EBEFFE]">
                <Layers className="h-4 w-4 text-brand-primary" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-semibold leading-tight text-[#16181D]">
                  ¿Tenés varios productos?
                </span>
                <span className="mt-0.5 block text-[12.5px] leading-snug text-black/50">
                  Subilos juntos: cada foto es un producto
                </span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-brand-primary transition-transform group-hover:translate-x-0.5" />
            </button>
          ) : undefined
        }
      />
      {campoNombre}
    </div>
  );

  const subtitulo = editingVariant
    ? "Cambiá el nombre o sus fotos"
    : inBulkMode
      ? null
      : "Subí sus fotos y empezá a crear con él";

  /* ── Shared action buttons ── */
  const deleteButton =
    editingVariant && onDeleteVariant ? (
      <button
        type="button"
        onClick={() => setShowDeleteConfirm(true)}
        disabled={loading}
        aria-label="Eliminar producto"
        className={`inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-full px-3 text-[14px] font-medium text-error transition-colors hover:bg-error-soft max-sm:w-10 max-sm:border max-sm:border-black/[0.08] max-sm:px-0 ${loading ? "cursor-not-allowed opacity-60" : ""}`}
      >
        <Trash2 className="h-4 w-4" />
        <span className="hidden sm:inline">Eliminar</span>
      </button>
    ) : (
      <span />
    );

  const saveButton = (fullWidth: boolean) => (
    <button
      type="button"
      onClick={handleSaveWithValidation}
      disabled={anySaving || isSaveDisabled}
      className={`inline-flex h-10 items-center justify-center gap-2 rounded-full px-5 text-[14px] font-semibold transition-colors ${fullWidth ? "h-11 flex-1 text-[15px]" : ""} ${isSaveDisabled ? "cursor-not-allowed bg-black/[0.05] text-black/30" : "bg-brand-primary text-white shadow-[0_6px_16px_-6px_rgba(22,68,235,0.55)] hover:bg-brand-primary-hover"}`}
    >
      {bulk.saving && <Loader2 className="h-4 w-4 animate-spin" />}
      {saveButtonLabel}
    </button>
  );

  const closeButton = (
    <button
      type="button"
      onClick={handleClose}
      disabled={anySaving}
      aria-label="Cerrar"
      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-black/[0.05] text-black/50 transition-colors hover:bg-black/[0.09] hover:text-black/80 disabled:opacity-50"
    >
      <X className="h-4 w-4" />
    </button>
  );

  const encabezado = (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h3 className="text-[19px] font-semibold leading-tight tracking-[-0.01em] text-[#16181D]">
          {title}
        </h3>
        {subtitulo && (
          <p className="mt-1 text-[13.5px] leading-snug text-black/45">
            {subtitulo}
          </p>
        )}
      </div>
      {closeButton}
    </div>
  );

  // El panel y la hoja tienen el fondo de Mi marca (`#F9F9F9`, `TEMAS.claro.fondo` de `EstiloMinimal`):
  // se abren desde esa pantalla y tienen que ser la misma superficie.

  /* ── Delete confirmation modal ── */
  const deleteConfirmModal =
    editingVariant && onDeleteVariant && showDeleteConfirm ? (
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]">
        <div className="w-full max-w-sm rounded-3xl bg-white p-6 text-center shadow-[0_24px_64px_-16px_rgba(16,24,40,0.35)] animate-modal-appear">
          <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-error-soft">
            <AlertCircle className="h-5 w-5 text-error" />
          </div>
          <h3 className="text-[17px] font-semibold text-[#16181D]">
            Eliminar producto
          </h3>
          <p className="mt-1 text-[14px] leading-snug text-black/55">
            ¿Seguro que querés eliminar este producto? Esta acción no se puede
            deshacer.
          </p>
          <div className="mt-5 flex gap-2">
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(false)}
              className="inline-flex h-10 flex-1 items-center justify-center rounded-full border border-black/[0.08] bg-white px-5 text-[14px] font-medium text-[#16181D] transition-colors hover:bg-black/[0.03]"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => {
                onDeleteVariant(editingVariant.id);
                setShowDeleteConfirm(false);
              }}
              disabled={loading}
              className={`inline-flex h-10 flex-1 items-center justify-center rounded-full bg-error px-5 text-[14px] font-medium text-white transition-colors hover:bg-red-600 ${loading ? "cursor-not-allowed opacity-75" : ""}`}
            >
              Eliminar
            </button>
          </div>
        </div>
      </div>
    ) : null;

  return ReactDOM.createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[60] transition-opacity duration-300 ${animated ? "bg-black/40 opacity-100" : "bg-black/40 opacity-0"}`}
        onClick={handleClose}
      />

      {/* Subir varios: un solo contenedor, panel desde `sm` y hoja abajo. */}
      {enVarios && (
        <div
          className="fixed z-[61] flex flex-col bg-[#F9F9F9] inset-x-0 bottom-0 rounded-t-[24px] h-[calc(100dvh-1rem-env(safe-area-inset-top,0px))] shadow-[0_-12px_40px_-12px_rgba(16,24,40,0.3)] sm:inset-x-auto sm:left-auto sm:top-4 sm:right-4 sm:bottom-4 sm:h-auto sm:w-[440px] sm:rounded-[24px] sm:shadow-[0_24px_64px_-16px_rgba(16,24,40,0.35)] sm:ring-1 sm:ring-black/[0.04]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="py-2 shrink-0 sm:hidden">
            <div className="mx-auto h-[5px] w-9 rounded-full bg-black/[0.12]" />
          </div>
          {subirVarios}
        </div>
      )}

      {/* Desktop: slide from right */}
      {!enVarios && (
      <div
        className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[440px] z-[61] flex-col overflow-hidden rounded-[24px] bg-[#F9F9F9] shadow-[0_24px_64px_-16px_rgba(16,24,40,0.35)] ring-1 ring-black/[0.04] transition-transform duration-300 ease-out ${
          animated ? "translate-x-0" : "translate-x-[calc(100%+32px)]"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 px-6 pb-4 pt-6">{encabezado}</div>
        <div className="flex-1 relative overflow-hidden">
          <div
            className={`absolute inset-0 overflow-y-auto px-6 pb-6 pt-2 transition-all duration-300 ease-out ${
              loading
                ? "opacity-0 -translate-y-3 pointer-events-none"
                : "opacity-100 translate-y-0"
            }`}
          >
            {formContent}
          </div>
          {savingState}
        </div>
        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-black/[0.06] px-6 py-4">
          {deleteButton}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={anySaving}
              className={`inline-flex h-10 items-center rounded-full px-4 text-[14px] font-medium text-black/55 transition-colors hover:bg-black/[0.04] hover:text-[#16181D] ${anySaving ? "cursor-not-allowed opacity-50" : ""}`}
            >
              Cancelar
            </button>
            {saveButton(false)}
          </div>
        </div>
      </div>
      )}

      {/* Mobile: bottom sheet */}
      {!enVarios && (
      <div
        className="sm:hidden fixed inset-x-0 bottom-0 z-[61] flex flex-col rounded-t-[24px] bg-[#F9F9F9] shadow-[0_-12px_40px_-12px_rgba(16,24,40,0.3)]"
        style={{
          // Restamos `env(safe-area-inset-top)` para que el tope del
          // sheet quede por debajo de la status bar translúcida (PWA
          // standalone con viewport-fit=cover).
          height: "calc(100dvh - 1rem - env(safe-area-inset-top, 0px))",
          transform:
            dragY > 0
              ? `translateY(${dragY}px)`
              : animated
                ? "translateY(0)"
                : "translateY(100%)",
          transition: dragY === 0 ? "transform 0.3s ease-out" : "none",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle + encabezado: los dos arrastran para cerrar. */}
        <div
          className="shrink-0 cursor-grab px-4 pb-3 active:cursor-grabbing"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="py-2">
            <div className="mx-auto h-[5px] w-9 rounded-full bg-black/[0.12]" />
          </div>
          {encabezado}
        </div>
        {/* Scrollable content */}
        <div className="flex-1 relative overflow-hidden">
          <div
            className={`absolute inset-0 overflow-y-auto px-4 pb-6 pt-2 transition-all duration-300 ease-out ${
              loading
                ? "opacity-0 -translate-y-3 pointer-events-none"
                : "opacity-100 translate-y-0"
            }`}
          >
            {formContent}
          </div>
          {savingState}
        </div>
        {/* Guardar a todo el ancho, al alcance del pulgar; eliminar al lado si se edita. */}
        <div
          className="flex shrink-0 items-center gap-2 border-t border-black/[0.06] px-4 pt-3"
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
        >
          {editingVariant && onDeleteVariant && deleteButton}
          {saveButton(true)}
        </div>
      </div>
      )}

      {deleteConfirmModal}

      {/* Hidden input used by "Subir varios productos" / "Agregar más".
          Note: `accept` uses only MIME types (no extension fallback) — iOS
          Safari and some Android pickers fall back to a single-selection
          mode when the accept list mixes MIME types with extensions like
          `.heic`, so the `multiple` attribute gets effectively ignored.
          HEIC is covered by the explicit `image/heic` / `image/heif` MIME
          types and our app converts these client-side regardless. */}
      {!editingVariant && (
        <input
          ref={bulk.fileInputRef}
          type="file"
          accept="image/*,image/heic,image/heif"
          multiple
          className="hidden"
          onChange={bulk.handleSelectFiles}
        />
      )}
    </>,
    document.body,
  );
};

export default VariantForm;
